export const POLL_SUPERSEDED = "superseded";
export const POLL_TIMED_OUT = "timeout";
/**
 * Polls a backend task until its status is no longer "processing". `fetchTask(taskId)` performs one poll.
 * `K` is whatever identifies the work to poll: one task id by default, or e.g. the ids of an upload's tasks.
 *
 * The poll resolves with the last fetched result, `{status: POLL_TIMED_OUT}` once timeoutMs has passed
 * while still processing, or `{status: POLL_SUPERSEDED}` when clearPolls runs or the caller's isCurrent
 * turns false. The last two carry only `status`, so every other field of T must be optional.
 * A failed fetch rejects the poll.
 */
export function useTaskPoll(fetchTask, { intervalMs, timeoutMs }) {
    const activePolls = new Set();
    const clearPolls = () => {
        activePolls.forEach((stop) => stop()); // each stop() settles its poll and removes itself, emptying the set
    };
    const pollTask = (taskId, isCurrent = () => true) => new Promise((resolve, reject) => {
        const startedAt = Date.now();
        let timer = null;
        const detach = () => {
            if (timer)
                clearTimeout(timer);
            activePolls.delete(stop);
        };
        const stop = () => {
            detach();
            resolve({ status: POLL_SUPERSEDED });
        };
        activePolls.add(stop);
        const tick = async () => {
            try {
                const result = await fetchTask(taskId);
                if (!activePolls.has(stop) || !isCurrent()) {
                    stop();
                }
                else if (result.status !== "processing") {
                    detach();
                    resolve(result);
                }
                else if (Date.now() - startedAt > timeoutMs) {
                    detach();
                    resolve({ status: POLL_TIMED_OUT });
                }
                else {
                    timer = setTimeout(tick, intervalMs);
                }
            }
            catch (e) {
                detach();
                reject(e);
            }
        };
        timer = setTimeout(tick, intervalMs);
    });
    return { pollTask, clearPolls };
}
